import { useCallback, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import * as AD from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';

/**
 * Confirmation for a destructive action (DESIGN.md §4, DES-13). A reversible
 * action never uses it: it runs and offers "Annuler" in a toast. A bulk
 * deletion or the deletion of an organisation passes `typedName`: the button
 * stays disabled until that exact name is typed.
 */
// components/ui/alert-dialog is untyped JS; give its parts a permissive type here.
type Loose = ComponentType<Record<string, unknown> & { children?: ReactNode }>;
const AlertDialog = AD.AlertDialog as unknown as Loose;
const AlertDialogContent = AD.AlertDialogContent as unknown as Loose;
const AlertDialogHeader = AD.AlertDialogHeader as unknown as Loose;
const AlertDialogTitle = AD.AlertDialogTitle as unknown as Loose;
const AlertDialogDescription = AD.AlertDialogDescription as unknown as Loose;
const AlertDialogFooter = AD.AlertDialogFooter as unknown as Loose;
const AlertDialogCancel = AD.AlertDialogCancel as unknown as Loose;
const AlertDialogAction = AD.AlertDialogAction as unknown as Loose;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel: string;
  onConfirm: () => void | Promise<void>;
  typedName?: string;
  busy?: boolean;
  /** 'default' for a confirmation that deletes nothing (a migration): no red button. */
  tone?: 'destructive' | 'default';
}

export function ConfirmDialog({ open, onOpenChange, title, description, confirmLabel, onConfirm, typedName, busy = false, tone = 'destructive' }: Props) {
  const { t } = useTranslation(['common']);
  const [typed, setTyped] = useState('');
  // Guards a double click on a destructive action, whatever the caller does with `busy`.
  const [running, setRunning] = useState(false);
  // An empty name to type would match an empty field: nothing to confirm with, so blocked.
  const blocked = busy || running || (typedName !== undefined && (!typedName.trim() || typed.trim() !== typedName.trim()));
  const runConfirm = async () => {
    if (blocked) return;
    setRunning(true);
    try {
      await onConfirm();
      setTyped('');
    } catch {
      // The caller reports the failure (toast); the dialog stays open, as typed,
      // so the action can be retried (DES-12).
    } finally {
      setRunning(false);
    }
  };
  return (
    <AlertDialog open={open} onOpenChange={(next: boolean) => { if (!next) setTyped(''); onOpenChange(next); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {/* An alert dialog needs a description for screen readers; without one, the title serves. */}
          {description
            ? <AlertDialogDescription>{description}</AlertDialogDescription>
            : <AlertDialogDescription className="sr-only">{title}</AlertDialogDescription>}
        </AlertDialogHeader>
        {typedName !== undefined && (
          <label className="block space-y-1 text-sm">
            <span>{t('common:confirmDialog.typeToConfirm', { name: typedName })}</span>
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" aria-label={t('common:confirmDialog.typeToConfirm', { name: typedName })} />
          </label>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy || running}>{t('common:actions.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            disabled={blocked}
            className={tone === 'destructive' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : undefined}
            onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); void runConfirm(); }}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

interface Ask {
  title: ReactNode;
  description?: ReactNode;
  confirmLabel: string;
  typedName?: string;
  tone?: 'destructive' | 'default';
}

/**
 * The browser's confirm() as a ConfirmDialog: `if (!(await ask({...}))) return;`.
 * Render `dialog` once in the component; Annuler, Échap or a click outside
 * answer false.
 */
export function useConfirmDialog(): [(ask: Ask) => Promise<boolean>, ReactNode] {
  const [pending, setPending] = useState<(Ask & { resolve: (ok: boolean) => void; id: number }) | null>(null);
  const seq = useRef(0);
  // A second ask while one is open answers the first one « no »: it never hangs.
  // Each ask is its own dialog (key): text typed for the first never carries
  // into the second (CONFIRM-1).
  const ask = useCallback((a: Ask) => new Promise<boolean>((resolve) => setPending((previous) => {
    previous?.resolve(false);
    seq.current += 1;
    return { ...a, resolve, id: seq.current };
  })), []);
  const dialog = pending ? (
    <ConfirmDialog
      key={pending.id}
      open
      onOpenChange={(open) => { if (!open) { pending.resolve(false); setPending(null); } }}
      title={pending.title}
      description={pending.description}
      confirmLabel={pending.confirmLabel}
      typedName={pending.typedName}
      tone={pending.tone}
      onConfirm={() => { pending.resolve(true); setPending(null); }}
    />
  ) : null;
  return [ask, dialog];
}
